import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, errorMessage } from '../../lib/api';
import { Banner, Button, Checkbox, CodeField, Eyebrow, Skeleton } from '../../ds/components';
import { attemptsText, type RecoveryCodesResponse, type TwoFactorSetup } from './auth';
import { RecoveryCodes } from './RecoveryCodes';
import styles from './TwoFactorSetupPanel.module.css';

export interface TwoFactorSetupPanelProps {
  /** `login`: full-frame during the first sign-in (eyebrow "Schritt 2 von 3"); `settings`: the E17 dialog. */
  variant: 'login' | 'settings';
  /** Called after the recovery codes were confirmed as saved. */
  onDone: () => void;
  /** "Abbrechen" (settings) or "Anderes Konto" (login). */
  onCancel: () => void;
  cancelLabel: string;
}

const setupQueryKey = ['2fa-setup'] as const;

/**
 * State of a 2FA setup: the pending secret (`POST /auth/2fa/setup`, fetched once), the code, the confirmation with
 * the attempts counter (a fresh secret after the fifth wrong code) and the recovery codes of the success. Shared by
 * this panel and the security step of the user wizard (Onboarding Nutzer 11 / M9).
 */
export function useTwoFactorSetup(options: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const setup = useQuery({
    queryKey: setupQueryKey,
    queryFn: () => api<TwoFactorSetup>('/auth/2fa/setup', { method: 'POST', body: {} }),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    // An account whose 2FA is already active must not request a new secret (the server answers 409).
    enabled: options.enabled ?? true,
  });
  const [code, setCode] = useState('');
  const [attempts, setAttempts] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  const confirm = useMutation({
    mutationFn: (value: string) =>
      api<RecoveryCodesResponse>('/auth/2fa/confirm', { method: 'POST', body: { code: value } }),
    onSuccess: (result) => {
      setCodes(result.recoveryCodes);
      setAttempts(null);
    },
    onError: async (error) => {
      setCode('');
      setAttempts(attemptsText(error));
      if (error instanceof ApiError) {
        const used = error.detailNumber('attemptsUsed');
        const max = error.detailNumber('attemptsMax');
        // After the fifth wrong code the server discards the pending secret: a fresh QR code is needed.
        if (used !== null && max !== null && used >= max) {
          await queryClient.invalidateQueries({ queryKey: setupQueryKey });
          setAttempts(null);
        }
      }
    },
  });

  const fieldError =
    confirm.isError && confirm.error instanceof ApiError
      ? (confirm.error.fields['code'] ?? confirm.error.message)
      : confirm.isError
        ? errorMessage(confirm.error)
        : undefined;

  return { setup, code, setCode, attempts, codes, confirm, fieldError };
}

/** Einstellungen 17 / M8: QR code and key on the left, the six digits on the right, then the codes. */
export function TwoFactorSetupPanel({ variant, onDone, onCancel, cancelLabel }: TwoFactorSetupPanelProps) {
  const { setup, code, setCode, attempts, codes, confirm, fieldError } = useTwoFactorSetup();
  const [saved, setSaved] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (code.length === 6 && !confirm.isPending) confirm.mutate(code);
  }

  const eyebrow = variant === 'settings' ? 'Sicherheit · Schritt 2 von 3' : 'Schritt 2 von 3';

  if (codes) {
    return (
      <div className={styles.codesStep}>
        <div className={styles.text}>
          <Eyebrow>{variant === 'settings' ? 'Sicherheit · Schritt 3 von 3' : 'Schritt 3 von 3'}</Eyebrow>
          <h2 className={styles.title}>Wiederherstellungscodes</h2>
          <p className={styles.description}>
            Zehn Codes, jeder gilt einmal. Bewahren Sie sie außerhalb des Handys auf — sie ersetzen die App,
            wenn Sie keinen Zugriff darauf haben.
          </p>
        </div>
        <RecoveryCodes codes={codes} />
        <Checkbox
          label="Ich habe die Codes kopiert oder gespeichert."
          checked={saved}
          onChange={(event) => setSaved(event.target.checked)}
        />
        <div className={styles.footer}>
          <Button disabled={!saved} onClick={onDone}>
            {variant === 'settings' ? 'Fertig' : 'Weiter'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className={styles.grid} onSubmit={submit} noValidate>
      <div className={styles.left}>
        <div className={styles.qrFrame}>
          {setup.data ? (
            <img className={styles.qr} src={setup.data.qrDataUrl} alt="QR-Code für die Authenticator-App" />
          ) : (
            <Skeleton variant="block" width="100%" height="100%" />
          )}
        </div>
        <div className={styles.key}>
          Oder Schlüssel eingeben:
          <br />
          {setup.data ? (
            <span className={styles.keyGroups}>{setup.data.keyGroups.join(' · ')}</span>
          ) : (
            <Skeleton width={120} className={styles.keySkeleton} />
          )}
        </div>
      </div>
      <div className={styles.right}>
        <div className={styles.text}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className={styles.title}>Code aus der App eingeben</h2>
          <p className={styles.description}>
            Scannen Sie den Code mit einer Authenticator-App und geben Sie die sechs Ziffern ein.
          </p>
        </div>
        {setup.isError ? (
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void setup.refetch()}>
                Erneut versuchen
              </Button>
            }
          >
            {errorMessage(setup.error)}
          </Banner>
        ) : null}
        <CodeField
          label="Code aus der App"
          labelHidden
          name="code"
          autoFocus
          value={code}
          onChange={setCode}
          onComplete={(value) => confirm.mutate(value)}
          error={fieldError}
          disabled={confirm.isPending || !setup.data}
          className={styles.codeField}
        />
        <div className={styles.footer}>
          {attempts ? <span className={styles.attempts}>{attempts}</span> : null}
          <span className={styles.footerActions}>
            <Button variant="secondary" onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button type="submit" loading={confirm.isPending} disabled={code.length < 6}>
              Prüfen
            </Button>
          </span>
        </div>
      </div>
    </form>
  );
}
